import React, { useState, useEffect, useRef } from 'react'
import V2Layout from '../components/V2Layout'
import SEO from '../components/SEO'
import CapabilitySections from './capabilities/CapabilitySections'
import useCompanyStats from '../components/useCompanyStats'
import team1 from '../images/team-1.png'
import team2 from '../images/team-2.png'
import team3 from '../images/team-3.png'
import team4 from '../images/team-4.png'
import team5 from '../images/team-5.png'
import '../versions/v2/story-timelines.css'

const NODES = [
    { yr: 2004, label: 'Founded', era: '2004–2005 · Auburn, CA', heading: 'Founded: Engineering & Design', body: "Launched in a 2,000 sq ft workshop, delivering engineering design work and the company's first launch vehicle hardware.", chips: ['2,000 sq ft', 'Auburn, CA'], programs: 'Falcon 1 · Merlin 1A · BE1 H₂O₂ engine', color: '#4A9ED6' },
    { yr: 2007, label: 'First CNC', era: '2007 · Auburn, CA', heading: 'First CNC Machining', body: 'Expanded to 3,000 sq ft and brought precision machining in-house with the first Mazak CNC lathe - still running today.', chips: ['3,000 sq ft', '1st Mazak Lathe'], programs: '', color: '#3DB990' },
    { yr: 2010, label: 'Autoflow Acq.', era: '2008–2010', heading: 'Autoflow Products Acquisition', body: 'Acquired Autoflow Products Co., adding flow switches to the portfolio, and expanded into maritime submarine regulators.', chips: ['Flow Switches', 'Maritime Systems'], programs: 'Falcon 9 · Dragon Cargo · Merlin 1D · New Shepard · BE3 · NASA Stennis', color: '#D4537E' },
    { yr: 2011, label: 'Dome Regs', era: '2011 · Auburn, CA', heading: 'Dome-Loaded Regulators', body: 'Added a second Mazak CNC lathe and launched the dome-loaded regulator product line for high-pressure applications.', chips: ['2nd Mazak', 'Dome Regulators'], programs: '', color: '#9590D0' },
    { yr: 2012, label: 'Cryo + Clean', era: '2012 · Auburn, CA', heading: 'Cryogenic Capability & Clean Room', body: 'Grew to 5,000 sq ft, established cryogenic butterfly valve production, and built a clean room.', chips: ['5,000 sq ft', 'Cryo Valves', 'Clean Room'], programs: 'Falcon Heavy · Crew Dragon · Starship · BE4 engine · Launcher One', color: '#D4A84B' },
    { yr: 2016, label: 'Rocklin', era: '2016 · Rocklin, CA', heading: 'Relocation to Rocklin', body: 'Outgrew Auburn and moved to a 15k sq ft facility in Rocklin, CA, adding a 4-axis Mazak mill.', chips: ['15k sq ft', 'Rocklin, CA', '4-axis Mill'], programs: 'Raptor engine · New Glenn · Dream Chaser · Firefly Alpha · RS1', color: '#E07B5A' },
    { yr: 2018, label: 'Swiss + 5-axis', era: '2017–2018', heading: 'Advanced Machining Investment', body: 'Added 5-axis milling, a Swiss-type screw machine, and robotic passivation for tighter tolerances and higher throughput.', chips: ['5-axis Mill', 'Swiss Machine', 'Robotic Passivation'], programs: '', color: '#7F77DD' },
    { yr: 2019, label: 'Lincoln Campus', era: '2019 · Lincoln, CA', heading: 'Lincoln Campus & Martin Testing Labs', body: "The company's largest expansion - 77k sq ft in Lincoln, CA - plus the acquisition of Martin Testing Labs.", chips: ['77k sq ft', 'Lincoln, CA', '+ Martin Testing'], programs: '', color: '#4A9ED6' },
    { yr: 2020, label: 'CNC Expansion', era: '2020 · Lincoln, CA', heading: 'Additional CNC Capacity', body: 'Added 7 additional CNC machines to meet growing production demand.', chips: ['+7 CNCs'], programs: '', color: '#4FB88A' },
	{ yr: 2024, label: 'Expanded Production Capacity', era: '2024· Lincoln, CA', heading: 'Expanded Production Capacity', body: 'Expanded manufacturing and testing capabilities—including 13 new CNC machines, larger clean rooms, and upgraded flow/high-pressure cells—alongside relocating Martin Test Labs to the Aviation Blvd facility', chips: ['+13 CNCs', 'Relocate Martin Test Labs', 'Expanded Clean Room'], programs: 'Crew Dragon · Starship/Raptor · New Glenn · Blue Moon · Vulcan · Neutron', color: '#5BBF5B' },
]

const ERAS = [
    { period: '2004 – 2006', desc: 'Engineering design · First launch vehicle hardware', tags: ['Falcon 1', 'Merlin 1A', 'BE1 H₂O₂ engine'] },
    { period: '2008 – 2010', desc: 'Launch vehicle valves, maritime regs, cryo & throttling', tags: ['Falcon 9', 'Dragon Cargo', 'Merlin 1D', 'New Shepard', 'BE3 engine', 'Maritime sub regs', 'NASA Stennis', 'NASA Marshall'] },
    { period: '2012 – 2015', desc: 'Cryo butterfly valves, large throttling, production ramp', tags: ['Falcon Heavy', 'Crew Dragon', 'Starship', 'BE4 engine', 'Launcher One'] },
    { period: '2016 – 2018', desc: 'Larger dome regs, updated clean room, Rocklin facility', tags: ['Raptor engine', 'New Glenn', 'Hadley engine', 'Dream Chaser', 'Firefly Alpha', 'RS1'] },
    { period: '2020 – Present', desc: 'Full production scale - cryo, ambient, relief, sleeve valves & regulators across the industry', tags: ['Crew Dragon', 'Starship / Raptor', 'BE3U engine', 'BE7 engine', 'Blue Moon Lander', 'XB-1', 'Terran 1', 'Terran R', 'Nova (Stoke)', 'Talon-A', 'Vulcan', 'Neutron', 'Haven-1', 'Winnebago-1', 'VIP aircraft completions'] },
]

function hexRgb(h) { return [1, 3, 5].map(i => parseInt(h.slice(i, i + 2), 16)).join(',') }

// TODO: Replace placeholder roles, bios, and initials with real team members before go-live.
const TEAM = [
    { role: 'Director of Manufacturing', bio: '15+ years in aerospace manufacturing. Oversees all shop floor operations and ensures every part meets flight-ready standards.', initials: 'DM', photo: team1 },
    { role: 'Engineering Manager', bio: 'Leads the engineering team with deep expertise in CNC process optimization and DFM for complex aerospace geometries.', initials: 'EM', photo: team2 },
    { role: 'Quality Assurance Manager', bio: 'AS9100 lead auditor with a passion for building quality systems that empower machinists to do their best work.', initials: 'QM', photo: team3 },
    { role: 'Operations Manager', bio: 'Keeps production moving, from scheduling and logistics to continuous improvement initiatives across the facility.', initials: 'OM', photo: team4 },
    { role: 'HR & Culture Manager', bio: 'Drives recruiting, onboarding, and the culture programs that make PFC a great place to work.', initials: 'HR', photo: team5 },
]

function TeamCard({ member }) {
    return (
        <div className="pfc-team-card">
            <img src={member.photo} alt={member.role} className="pfc-team-card-photo" />
            <h3 className="pfc-team-card-role">{member.role}</h3>
            <p className="pfc-team-card-bio">{member.bio}</p>
        </div>
    )
}

const AboutScreen = () => {
    const { stats } = useCompanyStats()
    const [isMobile, setIsMobile] = useState(false)
    const stageRef = useRef(null)
    const outerRef = useRef(null)
    const verticalStageRef = useRef(null)

    // Responsive layout detector
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 1024)
        }
        handleResize()
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    // Horizontal timeline initializer
    useEffect(() => {
        if (isMobile) return
        if (!stageRef.current || !outerRef.current) return

        const stage = stageRef.current
        stage.innerHTML = '<div class="stl-spine"></div>'

        const YS = 2003, YE = 2026, PL = 60, PR = 90
        const outer = outerRef.current
        const avail = outer.clientWidth - 80 // approximate padding
        const PPY = (avail - PL - PR) / (YE - YS)
        stage.style.width = avail + 'px'
        const xf = yr => PL + (yr - YS) * PPY

        // Year ticks
        for (let y = YS + 1; y <= YE; y++) {
            if (y % 2) continue
            const t = document.createElement('div')
            t.className = 'stl-tick'
            t.style.left = xf(y) + 'px'
            t.innerHTML = `<div class="stl-tick-mark"></div><div class="stl-tick-yr">${y}</div>`
            stage.appendChild(t)
        }

        // Nodes
        NODES.forEach((n, i) => {
            const side = i % 2 === 0 ? 'above' : 'below'
            const node = document.createElement('div')
            node.className = 'stl-node'
            node.style.left = xf(n.yr) + 'px'
            node.style.setProperty('--dot-c', n.color)
            const r = hexRgb(n.color)
            const chipsHtml = n.chips.map(c => `<span class="stl-chip" style="color:${n.color};border-color:rgba(${r},.35);background:rgba(${r},.09);">${c}</span>`).join('')
            const prog = n.programs ? `<p class="stl-programs">${n.programs}</p>` : ''
            node.innerHTML = `
                <div class="stl-dot"></div>
                <div class="stl-popup stl-pop-${side}">
                    <div class="stl-card">
                        <div class="stl-card-label">
                            <span class="stl-card-yr">${n.yr}</span>
                            <span class="stl-card-title">${n.label}</span>
                        </div>
                        <div class="stl-card-detail">
                            <p class="stl-detail-era">${n.era}</p>
                            <p class="stl-detail-heading">${n.heading}</p>
                            <p class="stl-detail-body">${n.body}</p>
                            <div class="stl-chips">${chipsHtml}</div>
                            ${prog}
                        </div>
                    </div>
                    <div class="stl-stem"></div>
                </div>`
            node.addEventListener('mouseenter', () => {
                stage.querySelectorAll('.stl-node.expanded').forEach(x => x.classList.remove('expanded'))
                node.classList.add('expanded')
            })
            stage.appendChild(node)
        })

        // Auto-expand first node
        stage.querySelector('.stl-node')?.classList.add('expanded')

        // Drag to scroll
        let dn = false, sx, sl
        const onMouseDown = e => { dn = true; sx = e.pageX - outer.offsetLeft; sl = outer.scrollLeft }
        const onMouseLeave = () => { dn = false }
        const onMouseUp = () => { dn = false }
        const onMouseMove = e => {
            if (!dn) return; e.preventDefault()
            outer.scrollLeft = sl - (e.pageX - outer.offsetLeft - sx) * 1.1
        }

        outer.addEventListener('mousedown', onMouseDown)
        outer.addEventListener('mouseleave', onMouseLeave)
        outer.addEventListener('mouseup', onMouseUp)
        outer.addEventListener('mousemove', onMouseMove)

        return () => {
            if (outer) {
                outer.removeEventListener('mousedown', onMouseDown)
                outer.removeEventListener('mouseleave', onMouseLeave)
                outer.removeEventListener('mouseup', onMouseUp)
                outer.removeEventListener('mousemove', onMouseMove)
            }
        }
    }, [isMobile])

    // Vertical timeline initializer
    useEffect(() => {
        if (!isMobile) return
        if (!verticalStageRef.current) return

        const stage = verticalStageRef.current
        stage.innerHTML = '<div class="svtl-spine"></div>'

        const YS = 2003, YE = 2026, PPY = 55, PT = 40, PB = 60
        stage.style.height = (PT + (YE - YS) * PPY + PB) + 'px'
        const yf = yr => PT + (yr - YS) * PPY

        // Year ticks
        for (let y = YS + 1; y <= YE; y++) {
            if (y % 2) continue
            const t = document.createElement('div')
            t.className = 'svtl-tick'
            t.style.top = yf(y) + 'px'
            t.innerHTML = `<div class="svtl-tick-mark"></div><div class="svtl-tick-yr">${y}</div>`
            stage.appendChild(t)
        }

        // Nodes
        NODES.forEach((n, i) => {
            const side = i % 2 === 0 ? 'right' : 'left'
            const node = document.createElement('div')
            node.className = 'svtl-node'
            node.style.top = yf(n.yr) + 'px'
            node.style.setProperty('--dot-c', n.color)
            const r = hexRgb(n.color)
            const chipsHtml = n.chips.map(c => `<span class="stl-chip" style="color:${n.color};border-color:rgba(${r},.35);background:rgba(${r},.09);">${c}</span>`).join('')
            const prog = n.programs ? `<p class="stl-programs">${n.programs}</p>` : ''
            node.innerHTML = `
                <div class="svtl-dot"></div>
                <div class="svtl-popup svtl-pop-${side}">
                    <div class="svtl-card">
                        <div class="stl-card-label svtl-card-label">
                            <span class="stl-card-yr">${n.yr}</span>
                            <span class="stl-card-title">${n.label}</span>
                        </div>
                        <div class="stl-card-detail">
                            <p class="stl-detail-era">${n.era}</p>
                            <p class="stl-detail-heading">${n.heading}</p>
                            <p class="stl-detail-body">${n.body}</p>
                            <div class="stl-chips">${chipsHtml}</div>
                            ${prog}
                        </div>
                    </div>
                    <div class="svtl-stem"></div>
                </div>`
            node.addEventListener('mouseenter', () => {
                stage.querySelectorAll('.svtl-node.expanded').forEach(x => x.classList.remove('expanded'))
                node.classList.add('expanded')
            })
            stage.appendChild(node)
        })

        // Auto-expand first node
        stage.querySelector('.svtl-node')?.classList.add('expanded')
    }, [isMobile])

    return (
        <V2Layout pageClass={`pfc-about ${isMobile ? 'pfc-story-v2' : 'pfc-story-v1'}`}>
            <SEO
                title="About Us"
                description="Supplying critical fluid control components for the most demanding sectors. With over 20 years of experience, PFC designs, manufactures, and tests components for launch vehicles and ground support applications."
            />
            {/* 1. Hero Section */}
            <section className="pfc-page-hero">
                <div className="pfc-page-hero-inner">
                    <p className="v2-section-label">About Precision Fluid Controls</p>
                    <h1 className="pfc-page-hero-title">Supplying Critical Fluid Control Components for the Most Demanding Sectors</h1>
                    <p className="pfc-page-hero-sub">
                        With over 20 years of experience, PFC designs, manufactures, and tests components for
                        launch vehicles and ground support applications. From AS9100D-certified production to
                        in-house cleanroom assembly, every component meets the highest aerospace and defense standards.
                    </p>
                </div>
            </section>

            {/* 2. Stats Bar */}
            <section className="v2-stats-bar">
                <div className="v2-stats-inner cols-4">
                    <div className="v2-stat"><span className="v2-stat-num">{stats.year_founded}</span><span className="v2-stat-label">Founded</span></div>
                    <div className="v2-stat"><span className="v2-stat-num">2</span><span className="v2-stat-label">Acquisitions</span></div>
                    <div className="v2-stat"><span className="v2-stat-num">{stats.facility_size}</span><span className="v2-stat-label">Sq Ft Today</span></div>
                    <div className="v2-stat"><span className="v2-stat-num">{stats.cnc_total}</span><span className="v2-stat-label">CNC Machines</span></div>
                </div>
            </section>

            {/* 3. What We Do */}
            <section className="v2-intro">
                <p className="v2-section-label">What We Do</p>
                <h2 className="v2-intro-title">
                    Valves &amp; Pressure Controls for Extreme Environments
                </h2>
                <p className="v2-intro-body">
                    Precision Fluid Controls, Inc. designs, manufactures and tests valves and pressure controls for
                    aerospace propulsion, flight and ground applications. With a large portfolio of products, we can
                    quickly provide solutions for many pressure control requirements, or create new products based on our
                    customers&apos; specifications. Products include main engine valves, vent/relief valves, pressure reducing
                    regulators, check valves and high pressure components.
                </p>
            </section>

            {/* 4. Our Story Timeline & Program Eras */}
            {!isMobile ? (
                /* Horizontal Timeline (Large Screens) */
                <>
                    <section className="stl-section">
                        <div className="stl-hint">
                            <p className="v2-section-label">Our Story · 2004 – 2026 · Hover a milestone node to expand · Scroll or drag to explore</p>
                        </div>
                        <div className="stl-outer" ref={outerRef}>
                            <div className="stl-stage" ref={stageRef} />
                        </div>
                    </section>

                    <section className="stl-eras-section">
                        <p className="v2-section-label">Programs Supported</p>
                        <h2 className="stl-eras-title" style={{ color: 'rgb(150, 120, 230)' }}>By Era</h2>
                        <div className="stl-eras-grid">
                            {ERAS.map((era, i) => (
                                <div className={`stl-era-card${i === ERAS.length - 1 ? ' stl-era-full' : ''}`} key={i}>
                                    <div className="stl-era-period">{era.period}</div>
                                    <p className="stl-era-desc">{era.desc}</p>
                                    <div className="stl-era-tags">
                                        {era.tags.map(t => <span className="stl-era-tag" key={t}>{t}</span>)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                </>
            ) : (
                /* Vertical Timeline (Mobile / Tablet Screens) */
                <section className="svtl-section">
                    <div className="svtl-layout">
                        <div className="svtl-timeline-col">
                            <p className="v2-section-label" style={{ marginBottom: '1.5rem', paddingLeft: '2rem' }}>Our Story · 2004 – 2026 · Hover a milestone node to expand</p>
                            <div className="svtl-stage" ref={verticalStageRef} />
                        </div>
                        <div className="svtl-eras-col">
                            <p className="v2-section-label">Programs Supported</p>
                            <h2 className="stl-eras-title" style={{ color: 'rgb(150, 120, 230)' }}>By Era</h2>
                            <div className="stl-eras-grid stl-eras-grid--vertical">
                                {ERAS.map((era, i) => (
                                    <div className="stl-era-card" key={i}>
                                        <div className="stl-era-period">{era.period}</div>
                                        <p className="stl-era-desc">{era.desc}</p>
                                        <div className="stl-era-tags">
                                            {era.tags.map(t => <span className="stl-era-tag" key={t}>{t}</span>)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* 5. Who We Serve */}
            <section className="v2-content">
                <div className="v2-content-inner">
                    <div className="v2-content-col">
                        <p className="v2-section-label">Who We Serve</p>
                        <h2 className="v2-content-title">Installed Across Launch &amp; Ground Program</h2>
                        <p className="v2-content-body">
                            PFC has designed and built products for many major commercial launch companies as well as
                            ground facilities at Kennedy Space Center, Stennis Space Center, Marshall Space Flight Center,
                            Arnold AFB, Edwards AFB, Vandenberg AFB, NASA and others.
                        </p>
                    </div>
                    <div className="v2-content-col">
                        <div className="v2-img-wrap">
                            <img src="/images/who_we_serve.jpg" className="v2-img who-we-serve-img" alt="Precision components" />
                            <div className="v2-img-caption">Commitment to aerospace quality</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 6. Our Facility */}
            <section className="v2-content" style={{ paddingBottom: 0 }}>
                <div style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto' }}>
                    <p className="v2-section-label about-facility-label">Our Facility</p>
                    <h2 className="v2-content-title">Engineered &amp; Built In-House</h2>
                </div>
            </section>
            <CapabilitySections slug="about-facility" />

            {/* 7. Leadership / Meet the Team (Hidden)
            <section className="v2-approach" style={{ paddingBottom: '5rem' }}>
                <div className="v2-approach-inner">
                    <div className="v2-approach-header">
                        <p className="v2-section-label">Leadership</p>
                        <h2 className="v2-approach-title">Meet the team</h2>
                    </div>
                    <div className="pfc-team-grid">
                        {TEAM.map((m, i) => <TeamCard key={i} member={m} />)}
                    </div>
                </div>
            </section>
            */}

        </V2Layout>
    )
}

export default AboutScreen
